import { useMemo, useState, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import {
  ReactFlow, Background, Handle, MarkerType, Position,
  type Edge, type Node, type NodeTypes,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  Workflow, Mail, MessageSquare, Clock4, FileBox, Camera, CreditCard,
  Sparkles, AlertTriangle, Webhook, Cog, Brain, BookMarked, Layers,
  ShoppingBag, Gavel, CheckCircle2, ArrowRight, ChevronRight,
} from "lucide-react";
import { getFlowReadiness } from "../../lib/connectionStore";
import {
  PRECONFIGURED_FLOWS, FLOW_GROUPS,
  type PreconfiguredFlow, type FlowStep, type FlowStepKind,
} from "../../data/preconfiguredFlows";
import { AGENTIC_INTEGRATIONS, type AgenticIntegration } from "../../data/agenticIntegrations";
import { BrandIcon, type BrandSlug } from "../BrandIcon";
import { getConnection, isFlowEnabled, setFlowEnabled } from "../../lib/connectionStore";
import { ConnectAppSheet } from "./ConnectAppSheet";
import { getSession } from "../../lib/session";
import { cls } from "../../lib/cls";

/* Preconfigured flow gallery — every card is a real n8n workflow shape,
   rendered live with xyflow. Apple-style: cream gradients, soft
   shadows, generous spacing, motion entrance. */

const GROUP_ICONS: Record<PreconfiguredFlow["group"], JSX.Element> = {
  "Leads":        <Mail          className="w-3.5 h-3.5" />,
  "Project flow": <Clock4        className="w-3.5 h-3.5" />,
  "Design":       <FileBox       className="w-3.5 h-3.5" />,
  "Money":        <CreditCard    className="w-3.5 h-3.5" />,
  "Client chats": <MessageSquare className="w-3.5 h-3.5" />,
};

const TRIGGER_ICON: JSX.Element = <Camera className="w-3 h-3" />;

/* Step-kind glyphs + neutral chip tones. Apple-style: low chroma,
   one ring colour per kind, white background. Avoid pastel tone
   collisions with the connector pills below. */
const STEP_GLYPH: Record<FlowStepKind, JSX.Element> = {
  trigger:    <Webhook       className="w-3 h-3" />,
  automation: <Cog           className="w-3 h-3" />,
  agent:      <Brain         className="w-3 h-3" />,
  memory:     <BookMarked    className="w-3 h-3" />,
  moodboard:  <Layers        className="w-3 h-3" />,
  catalog:    <ShoppingBag   className="w-3 h-3" />,
  rule:       <Gavel         className="w-3 h-3" />,
  approval:   <CheckCircle2  className="w-3 h-3" />,
  out:        <ArrowRight    className="w-3 h-3" />,
};

const STEP_RING: Record<FlowStepKind, string> = {
  trigger:    "ring-foyr/40   text-foyr-deep",
  automation: "ring-border    text-ink-700",
  agent:      "ring-violet-300 text-violet-700",
  memory:     "ring-amber-300 text-amber-700",
  moodboard:  "ring-fuchsia-300 text-fuchsia-700",
  catalog:    "ring-emerald-300 text-emerald-700",
  rule:       "ring-sky-300   text-sky-700",
  approval:   "ring-fern/40    text-fern",
  out:        "ring-ink-300   text-ink-700",
};

/* Compact horizontal flow timeline — 3 to 5 visible stages, overflow
   collapsed into a ⋯ chip. Pure HTML; no canvas, no zoom, **wraps**
   so the last pill is never clipped at the card's right edge. */
function FlowTimeline({ steps, compact = true }: { steps: readonly FlowStep[]; compact?: boolean }): JSX.Element {
  // In compact mode (card preview) we show first / collapsed-middle /
  // pre-last / last — max 4 visible stages. In expanded mode (detail
  // sheet) we show every step.
  const visible: Array<{ key: string; step?: FlowStep; collapsed?: number }> = (() => {
    if (!compact || steps.length <= 4) return steps.map((s) => ({ key: s.id, step: s }));
    const first = steps[0]!;
    const last = steps[steps.length - 1]!;
    const secondToLast = steps[steps.length - 2]!;
    const middleCount = steps.length - 3;
    return [
      { key: first.id, step: first },
      { key: "collapsed", collapsed: middleCount },
      { key: secondToLast.id, step: secondToLast },
      { key: last.id, step: last },
    ];
  })();
  return (
    <div className={cls("px-4 py-3 border-t border-border/50", !compact && "py-4")}>
      {/* flex-wrap keeps every chip fully readable; gap-y handles the wrap line spacing */}
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-2">
        {visible.map((v, i) => (
          <div key={v.key} className="flex items-center gap-1.5">
            {v.step ? (
              <div
                className={cls(
                  "inline-flex items-center gap-1.5 rounded-full bg-cream pl-1.5 pr-2.5 py-1 ring-1 shadow-[0_1px_2px_rgba(0,0,0,0.03)]",
                  STEP_RING[v.step.kind],
                )}
                title={v.step.sub ? `${v.step.label} — ${v.step.sub}` : v.step.label}
              >
                <span className="grid place-items-center w-4 h-4 rounded-full bg-cream-dark text-current shrink-0">
                  {STEP_GLYPH[v.step.kind]}
                </span>
                <span className="text-[11px] font-medium text-ink-950 whitespace-nowrap">{v.step.label}</span>
              </div>
            ) : (
              <div className="inline-flex items-center gap-1 rounded-full bg-cream-dark/60 px-2 py-1 text-[10px] text-ink-500 ring-1 ring-border/70">
                <span className="font-mono tabular">·  ·  ·</span>
                <span>+{v.collapsed}</span>
              </div>
            )}
            {i < visible.length - 1 ? (
              <ChevronRight className="w-3 h-3 text-ink-400 shrink-0" />
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

/* Apple-style flow graph node — clickable, hover-lifted, selected-ringed.
   Used by the detail-sheet xyflow. The click bubbles up through xyflow's
   onNodeClick so the parent can open the step-detail card. */
function FlowGraphNode({ data, selected }: { data: { step: FlowStep }; selected?: boolean }): JSX.Element {
  const s = data.step;
  return (
    <div className="group cursor-pointer">
      <Handle type="target" position={Position.Left}  className="!w-2 !h-2 !bg-ink-300 !border-ink-300" />
      <Handle type="source" position={Position.Right} className="!w-2 !h-2 !bg-ink-300 !border-ink-300" />
      <div
        className={cls(
          "flex items-center gap-2 px-3 py-2 rounded-xl bg-cream ring-1 transition-all",
          "shadow-[0_1px_2px_rgba(0,0,0,0.04)]",
          "group-hover:-translate-y-0.5 group-hover:shadow-[0_4px_12px_-2px_rgba(0,0,0,0.10)]",
          selected && "ring-2 !ring-foyr shadow-[0_4px_14px_-2px_rgba(201,92,26,0.25)]",
          !selected && STEP_RING[s.kind],
        )}
        style={{ minWidth: 160 }}
      >
        <span className="grid place-items-center w-6 h-6 rounded-lg bg-cream-dark text-current shrink-0">
          {STEP_GLYPH[s.kind]}
        </span>
        <div className="min-w-0">
          <div className="text-[12px] font-semibold text-ink-950 leading-tight">{s.label}</div>
          {s.sub ? <div className="text-[10px] text-ink-500 leading-tight mt-0.5">{s.sub}</div> : null}
        </div>
      </div>
    </div>
  );
}

/* Step-kind → backing agent / skill description. Cited when the
   user clicks a node so they see *which* part of our agent fleet
   runs that step — never a mystery. */
const STEP_BACKING: Record<FlowStepKind, { agent: string; skill: string; protocol: string }> = {
  trigger:    { agent: "n8n webhook",           skill: "—",                              protocol: "HTTP webhook → A2A tasks/send" },
  automation: { agent: "n8n",                   skill: "(workflow node)",                protocol: "n8n internal" },
  agent:      { agent: "Copilot supervisor",    skill: "search.hybrid · email.draft…",   protocol: "AG-UI SSE + MCP tools/call" },
  memory:     { agent: "memory_recall",         skill: "memory.recall",                  protocol: "MCP tools/call" },
  moodboard:  { agent: "Render agent (FLUX)",   skill: "design.pre_render_audit",        protocol: "ComfyUI via tunnel" },
  catalog:    { agent: "Catalog binder",        skill: "search.hybrid",                  protocol: "MCP tools/call" },
  rule:       { agent: "Constraint agent",      skill: "(rule canvas)",                  protocol: "Rule predicates on graph nodes" },
  approval:   { agent: "Human (Maya)",          skill: "—",                              protocol: "Explicit human approval (CLAUDE.md §5)" },
  out:        { agent: "Output binder",         skill: "email.draft_with_citations",     protocol: "Gmail MCP (drafts only)" },
};

const flowNodeTypes: NodeTypes = { step: FlowGraphNode as unknown as NodeTypes[string] };

function buildSheetGraph(flow: PreconfiguredFlow): { nodes: Node[]; edges: Edge[] } {
  // 200px column spacing — comfortable for the modal width; the
  // xyflow fitView+padding takes care of centring.
  const nodes: Node[] = flow.steps.map((s, i) => ({
    id: s.id,
    type: "step",
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    position: { x: i * 220, y: 0 },
    data: { step: s } as Record<string, unknown>,
    draggable: false,
  }));
  const edges: Edge[] = flow.steps.slice(0, -1).map((s, i) => ({
    id: `${s.id}-${flow.steps[i + 1]!.id}`,
    source: s.id,
    target: flow.steps[i + 1]!.id,
    animated: true,
    type: "smoothstep",
    markerEnd: { type: MarkerType.ArrowClosed, color: "#94908a", width: 18, height: 18 },
    style: { stroke: "#94908a", strokeWidth: 1.8 },
  }));
  return { nodes, edges };
}

/* The real graph viz inside the detail sheet. Clickable nodes — the
   selection opens a step-detail card below that names the backing
   agent + skill + protocol. Wrapped in ExpandableGraphFrame so the
   designer can pop it to fullscreen + 3D. */
import { ExpandableGraphFrame } from "../integrations/ExpandableGraphFrame";
import type { Graph3DNode, Graph3DLink } from "../integrations/Graph3D";

function FlowGraph({ flow }: { flow: PreconfiguredFlow }): JSX.Element {
  const { nodes, edges } = useMemo(() => buildSheetGraph(flow), [flow]);
  const [selectedId, setSelectedId] = useState<string | null>(flow.steps[0]?.id ?? null);
  const selected = useMemo(
    () => flow.steps.find((s) => s.id === selectedId) ?? flow.steps[0] ?? null,
    [flow.steps, selectedId],
  );
  // Mark the selected xyflow node so its border highlights.
  const nodesWithSelection = useMemo<Node[]>(
    () => nodes.map((n) => ({ ...n, selected: n.id === selectedId })),
    [nodes, selectedId],
  );
  const backing = selected ? STEP_BACKING[selected.kind] : null;
  const graph3d = {
    nodes: flow.steps.map<Graph3DNode>((s) => ({ id: s.id, label: s.label, kind: s.kind, description: s.sub })),
    links: flow.steps.slice(0, -1).map<Graph3DLink>((s, i) => ({ source: s.id, target: flow.steps[i + 1]!.id, emphasis: "primary" })),
  };
  return (
    <div className="space-y-2">
      <ExpandableGraphFrame title={flow.name} subtitle={flow.one_liner} graph3d={graph3d}>
      <div
        className="rounded-2xl border border-border/60 bg-[radial-gradient(circle_at_30%_30%,rgba(201,92,26,0.04),transparent_70%)]"
        style={{ height: 220 }}
      >
        <ReactFlow
          nodes={nodesWithSelection}
          edges={edges}
          nodeTypes={flowNodeTypes}
          fitView
          fitViewOptions={{ padding: 0.18 }}
          panOnDrag={false}
          zoomOnScroll={false}
          zoomOnPinch={false}
          zoomOnDoubleClick={false}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable
          onNodeClick={(_e, n) => setSelectedId(n.id)}
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={22} size={1} color="#ece7dc" />
        </ReactFlow>
      </div>
      </ExpandableGraphFrame>
      {/* Step detail card — updates on every node click. */}
      {selected && backing ? (
        <motion.div
          key={selected.id}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.18 }}
          className="rounded-xl border border-border/70 bg-cream-dark/30 px-3.5 py-2.5"
        >
          <div className="flex items-start gap-3">
            <span className={cls(
              "grid place-items-center w-8 h-8 rounded-lg bg-cream ring-1",
              STEP_RING[selected.kind],
            )}>
              {STEP_GLYPH[selected.kind]}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[12.5px] font-semibold text-ink-950">{selected.label}</span>
                <span className="pill text-[9.5px] border border-border bg-cream text-ink-500 uppercase tracking-wider">{selected.kind}</span>
              </div>
              {selected.sub ? <div className="text-[11.5px] text-ink-700 leading-snug mt-0.5">{selected.sub}</div> : null}
              <div className="grid grid-cols-3 gap-3 mt-2 text-[10.5px]">
                <div>
                  <div className="uppercase tracking-wider text-ink-500 mb-0.5">Backed by</div>
                  <div className="text-ink-950 font-medium">{backing.agent}</div>
                </div>
                <div>
                  <div className="uppercase tracking-wider text-ink-500 mb-0.5">Skill</div>
                  <div className="text-ink-950 font-mono text-[10.5px]">{backing.skill}</div>
                </div>
                <div>
                  <div className="uppercase tracking-wider text-ink-500 mb-0.5">Protocol</div>
                  <div className="text-ink-950">{backing.protocol}</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      ) : null}
    </div>
  );
}

/* Detail sheet — opens when the card body is clicked. Shows every
   step, every connector with status, the underlying n8n workflow id,
   and a "Run sample" CTA. Backdrop + ESC close. */
function FlowDetailSheet({
  flow, apps, statuses, readiness, enabled, onClose, onPickApp, isAdmin,
}: {
  flow: PreconfiguredFlow;
  apps: AgenticIntegration[];
  statuses: Record<string, string>;
  readiness: { ready: boolean; missing: readonly string[]; total_required: number };
  enabled: boolean;
  onClose: () => void;
  onPickApp: (a: AgenticIntegration) => void;
  isAdmin: boolean;
}): JSX.Element {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Flow details — ${flow.name}`}
      className="fixed inset-0 z-50 grid place-items-center px-4 py-8 bg-ink-950/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.985 }}
        animate={{ opacity: 1, y: 0,  scale: 1 }}
        transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-3xl max-h-[88vh] overflow-y-auto rounded-3xl bg-cream shadow-[0_24px_60px_-12px_rgba(0,0,0,0.25)] border border-border/70"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 pt-5 pb-4 flex items-start justify-between gap-4 border-b border-border/40">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-ink-500 inline-flex items-center gap-1.5">
              {GROUP_ICONS[flow.group]} {flow.group}
            </div>
            <h3 className="text-[20px] font-semibold text-ink-950 leading-tight mt-1">{flow.name}</h3>
            <p className="text-[13px] text-ink-700 leading-snug mt-1.5">{flow.one_liner}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full w-8 h-8 grid place-items-center text-ink-500 hover:text-ink-950 hover:bg-cream-dark"
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="px-6 pt-4 pb-2 flex flex-wrap items-center gap-2">
          <span className="pill bg-foyr/10 border border-foyr/30 text-foyr-deep text-[11px] inline-flex items-center gap-1">
            {TRIGGER_ICON} trigger · {flow.trigger}
          </span>
          {readiness.ready ? (
            <span className="pill bg-fern/10 border border-fern/30 text-fern text-[11px] inline-flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-fern" /> ready
            </span>
          ) : (
            <span className="pill bg-mulberry/10 border border-mulberry/30 text-mulberry text-[11px] inline-flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> needs {readiness.missing.join(" · ")}
            </span>
          )}
          <span className={cls(
            "pill text-[11px] border inline-flex items-center gap-1",
            enabled ? "bg-fern/15 border-fern/40 text-fern" : "bg-cream-dark border-border text-ink-500",
          )}>
            <span className={cls("w-1.5 h-1.5 rounded-full", enabled ? "bg-fern" : "bg-ink-400")} />
            {enabled ? "On" : "Off"}
          </span>
          <span className="pill text-[10.5px] border border-border bg-cream-dark/40 text-ink-500 font-mono">
            workflow · fm-{flow.id}
          </span>
        </div>

        {/* Real flow graph — xyflow inside the modal where the canvas
            has room to breathe; never clips. */}
        <div className="px-6 pt-3 pb-1">
          <div className="text-[10.5px] uppercase tracking-wider text-ink-500 mb-2">Flow graph</div>
          <FlowGraph flow={flow} />
        </div>
        {/* And the readable timeline below for scan-ability. */}
        <FlowTimeline steps={flow.steps} compact={false} />

        {/* connectors with statuses */}
        {apps.length > 0 ? (
          <div className="px-6 pt-3 pb-2">
            <div className="text-[10.5px] uppercase tracking-wider text-ink-500 mb-2">Connectors ({apps.filter((a) => statuses[a.name] === "connected").length}/{apps.length})</div>
            <div className="flex flex-wrap gap-2">
              {apps.map((a) => (
                <button
                  key={a.name}
                  type="button"
                  onClick={() => onPickApp(a)}
                  disabled={!isAdmin}
                  className={cls(
                    "inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-cream border border-border/70 text-[12px] text-ink-700 hover:border-foyr/40 hover:shadow-sm transition-shadow",
                    !isAdmin && "opacity-70 cursor-not-allowed",
                  )}
                  title={isAdmin ? a.agenticAction : "Admin rights required"}
                >
                  <BrandIcon slug={a.brand as BrandSlug} group={a.group} size={16} title={a.name} />
                  <span className="font-medium text-ink-950">{a.name}</span>
                  <span className={cls(
                    "w-1.5 h-1.5 rounded-full",
                    statuses[a.name] === "connected" ? "bg-fern"
                    : statuses[a.name] === "connecting" ? "bg-amber-500 animate-pulse"
                    : statuses[a.name] === "error" ? "bg-foyr"
                    : "bg-ink-300",
                  )} />
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {/* what the agent does */}
        <div className="px-6 py-3 mt-2 border-t border-border/40 bg-cream-dark/30">
          <div className="text-[10.5px] uppercase tracking-wider text-ink-500 mb-1.5">What the agent does</div>
          <p className="text-[12.5px] text-ink-700 leading-snug">
            {apps[0]?.agenticAction ?? "Reads the project graph, applies the studio rules, drafts an action — always with citations and never auto-sent."}
          </p>
        </div>
      </motion.div>
    </div>
  );
}

function FlowCard({ flow, index, onPickApp, refreshSignal, isAdmin }: { flow: PreconfiguredFlow; index: number; onPickApp: (a: AgenticIntegration) => void; refreshSignal: number; isAdmin: boolean }): JSX.Element {
  const apps = useMemo(
    () => flow.apps.map((name) => AGENTIC_INTEGRATIONS.find((i) => i.name === name)).filter(Boolean),
    [flow.apps],
  );
  const [enabled, setEnabled] = useState<boolean>(() => isFlowEnabled(flow.id));
  function toggle(): void {
    if (!isAdmin) return;
    const next = !enabled;
    setEnabled(next);
    setFlowEnabled(flow.id, next);
  }
  // Pull connection statuses afresh on each render so the chip dots
  // update as the user connects/disconnects apps in the sheet.
  void refreshSignal;
  const statuses = useMemo(() => Object.fromEntries(apps.map((a) => [a!.name, getConnection(a!.name).status])), [apps, refreshSignal]);
  const connected = Object.values(statuses).filter((s) => s === "connected").length;
  const readiness = useMemo(() => getFlowReadiness(flow.required), [flow.required, refreshSignal]);
  const [open, setOpen] = useState(false);
  // ESC closes the detail sheet — proper modal hygiene.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  return (
    <motion.article
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className="rounded-2xl border border-border/70 bg-gradient-to-br from-cream via-cream to-cream-dark/30 shadow-[0_1px_3px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(0,0,0,0.08)] overflow-hidden cursor-pointer hover:shadow-[0_4px_12px_-2px_rgba(0,0,0,0.08),0_12px_32px_-12px_rgba(0,0,0,0.10)] hover:-translate-y-0.5 transition-all"
      onClick={() => setOpen(true)}
      role="button"
      tabIndex={0}
      aria-label={`Open ${flow.name} details`}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(true); } }}
    >
      {/* Header — one tight block. Group · title · one-liner · toggle. */}
      <header className="px-4 pt-3.5 pb-3 flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-ink-500 inline-flex items-center gap-1">
            {GROUP_ICONS[flow.group]} {flow.group}
          </div>
          <h3 className="text-[15px] font-semibold text-ink-950 leading-tight mt-0.5 truncate">{flow.name}</h3>
          <p className="text-[11.5px] text-ink-500 leading-snug mt-0.5 line-clamp-2">{flow.one_liner}</p>
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); toggle(); }}
          disabled={!isAdmin}
          title={isAdmin ? "Toggle this flow" : "Admin rights required"}
          className={cls(
            "shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold border transition-colors",
            enabled
              ? "bg-fern/15 border-fern/40 text-fern"
              : "bg-cream-dark border-border text-ink-500 hover:text-ink-950",
            !isAdmin && "opacity-50 cursor-not-allowed hover:text-ink-500",
          )}
          aria-pressed={enabled}
        >
          <span className={cls("w-1.5 h-1.5 rounded-full", enabled ? "bg-fern" : "bg-ink-400")} />
          {enabled ? "On" : "Off"}
        </button>
      </header>

      {/* Meta row — trigger + readiness in a single line above the diagram. */}
      <div className="px-4 pb-2 flex items-center gap-2 flex-wrap">
        <span className="pill bg-foyr/10 border border-foyr/30 text-foyr-deep text-[10px] inline-flex items-center gap-1 whitespace-nowrap">
          {TRIGGER_ICON} {flow.trigger}
        </span>
        {readiness.ready ? (
          <span className="pill bg-fern/10 border border-fern/30 text-fern text-[10px] inline-flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-fern" /> ready
          </span>
        ) : (
          <span className="pill bg-mulberry/10 border border-mulberry/30 text-mulberry text-[10px] inline-flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            needs {readiness.missing.join(" · ")}
          </span>
        )}
      </div>

      {/* Flow timeline — Apple-style: white pills, ring tones, no clipping. */}
      <FlowTimeline steps={flow.steps} />
      {apps.length > 0 ? (
        <footer className="px-4 py-2.5 border-t border-border/50 bg-cream-dark/30">
          <div className="flex items-center justify-between mb-1.5 gap-2">
            <span className="text-[10px] uppercase tracking-wider text-ink-500">Connect</span>
            <span className="text-[10px] tabular text-ink-500">
              <strong className={cls("text-ink-950", connected === 0 && "text-mulberry")}>{connected}</strong>/{apps.length}
            </span>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {apps.map((a) => a ? (
              <button
                key={a.name}
                onClick={(e) => { e.stopPropagation(); onPickApp(a); }}
                disabled={!isAdmin}
                className={cls(
                  "inline-flex items-center gap-1.5 pl-1 pr-2 py-0.5 rounded-full border bg-cream text-[10.5px] font-medium border-border/70 text-ink-700 hover:shadow-sm hover:border-foyr/40 transition-shadow",
                  !isAdmin && "opacity-70 cursor-not-allowed",
                )}
                title={isAdmin ? a.agenticAction : "Admin rights required to connect"}
              >
                <BrandIcon
                  slug={a.brand as BrandSlug}
                  group={a.group}
                  size={14}
                  title={a.name}
                />
                {a.name}
                <span className={cls(
                  "w-1.5 h-1.5 rounded-full ml-0.5",
                  statuses[a.name] === "connected" ? "bg-fern"
                  : statuses[a.name] === "connecting" ? "bg-amber-500 animate-pulse"
                  : statuses[a.name] === "error" ? "bg-foyr"
                  : "bg-ink-300",
                )} />
              </button>
            ) : null)}
          </div>
        </footer>
      ) : null}
      {open ? (
        <FlowDetailSheet
          flow={flow}
          apps={apps.filter(Boolean) as AgenticIntegration[]}
          statuses={statuses}
          readiness={readiness}
          enabled={enabled}
          onClose={() => setOpen(false)}
          onPickApp={(a) => { setOpen(false); onPickApp(a); }}
          isAdmin={isAdmin}
        />
      ) : null}
    </motion.article>
  );
}

export function FlowGallery(): JSX.Element {
  const [group, setGroup] = useState<PreconfiguredFlow["group"] | "all">("all");
  const [activeApp, setActiveApp] = useState<AgenticIntegration | null>(null);
  const [refreshSignal, setRefreshSignal] = useState(0);
  const isAdmin = getSession()?.isAdmin === true;
  const onPickApp = useCallback((a: AgenticIntegration) => {
    if (!isAdmin) return;
    setActiveApp(a);
  }, [isAdmin]);
  const rows = useMemo(
    () => group === "all" ? PRECONFIGURED_FLOWS : PRECONFIGURED_FLOWS.filter((f) => f.group === group),
    [group],
  );
  return (
    <section className="space-y-3">
      <header className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <div className="eyebrow inline-flex items-center gap-1.5">
            <Workflow className="w-3 h-3" /> Preconfigured automations
          </div>
          <h2 className="display-3 mt-0.5 text-ink-950">Six everyday flows. Already wired.</h2>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          <button
            onClick={() => setGroup("all")}
            className={cls(
              "pill text-[11px] border",
              group === "all"
                ? "bg-ink-950 text-cream border-ink-950"
                : "bg-cream-dark border-border text-ink-700 hover:border-foyr/40",
            )}
          >
            All ({PRECONFIGURED_FLOWS.length})
          </button>
          {FLOW_GROUPS.map((g) => {
            const count = PRECONFIGURED_FLOWS.filter((f) => f.group === g).length;
            return (
              <button
                key={g}
                onClick={() => setGroup(g)}
                className={cls(
                  "pill text-[11px] border inline-flex items-center gap-1",
                  group === g
                    ? "bg-ink-950 text-cream border-ink-950"
                    : "bg-cream-dark border-border text-ink-700 hover:border-foyr/40",
                )}
              >
                {GROUP_ICONS[g]} {g} <span className="opacity-60 tabular">({count})</span>
              </button>
            );
          })}
        </div>
      </header>
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
        {rows.map((f, i) => <FlowCard key={f.id} flow={f} index={i} onPickApp={onPickApp} refreshSignal={refreshSignal} isAdmin={isAdmin} />)}
      </div>
      <ConnectAppSheet
        app={activeApp}
        onClose={() => setActiveApp(null)}
        onChanged={() => setRefreshSignal((n) => n + 1)}
      />
      <div className="rounded-xl border border-border/60 bg-cream-dark/40 px-3 py-2 flex items-center gap-2 flex-wrap">
        <Sparkles className="w-3.5 h-3.5 text-foyr" />
        <span className="text-[12px] text-ink-700">
          Each flow already runs in the background. Use the tiles below to see which everyday tools they connect to.
        </span>
      </div>
    </section>
  );
}
